import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { backgroundImg } from "src/assets/assetsExport";
import { companyInfo } from "../../data/general";
import { submitContact } from "../../api/contact";

const SERVICES = [
  {
    title: "International tenants",
    description:
      "Guidance on referencing, right-to-rent documentation, and securing a home before you arrive in London.",
  },
  {
    title: "Relocation support",
    description:
      "Area shortlists, viewing itineraries, and practical advice on banks, utilities, and settling in.",
  },
  {
    title: "Schools & family moves",
    description:
      "Help finding rentals near independent and state schools, with honest advice on catchments and commute.",
  },
  {
    title: "Pet-friendly lets",
    description:
      "Support negotiating with landlords and identifying buildings with sensible pet policies.",
  },
];

export default function ConciergePage(): JSX.Element {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [wechat, setWechat] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      await submitContact({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        wechat: wechat.trim() || undefined,
        subject: "Concierge enquiry",
        sourcePage: "/concierge",
        enquiryType: "general",
      });
      setSuccess(true);
      setName("");
      setEmail("");
      setWechat("");
      setMessage("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send your message"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const mailtoHref = `mailto:${companyInfo.email}?subject=${encodeURIComponent(
    "Concierge enquiry"
  )}&body=${encodeURIComponent(
    "Name:\nEmail:\nWeChat ID:\n\nHow can we help?"
  )}`;

  return (
    <>
      <Helmet>
        <title>Concierge | Letting Engine</title>
        <meta
          name="description"
          content="Relocation and concierge support for international tenants moving to London — schools, pets, viewings, and WeChat contact."
        />
      </Helmet>

      <section className="min-h-[70vh] pt-40 pb-20 w-full relative flex items-center justify-center overflow-hidden text-center">
        <img
          src={backgroundImg.meeting1}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15 filter grayscale contrast-125 pointer-events-none z-0"
        />
        <div className="relative z-10 max-w-4xl px-6">
          <h1 className="font-teko leading-tight mb-6 tracking-[-2px] text-6xl">
            <span className="stroke-1 text-text-slate">Concierge</span>
          </h1>
          <p className="font-manrope text-base leading-relaxed text-text-slate max-w-2xl mx-auto">
            Moving to London from overseas? Our concierge team helps international
            tenants and relocating families find the right home — with practical
            support on schools, pets, and settling in.
          </p>
        </div>
      </section>

      <section className="py-24 px-[5%] bg-bg-porcelain">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="mb-12 pl-8 border-l-4 border-l-solid border-l-brand-yellow">
              <span className="font-manrope text-xs uppercase tracking-widest text-text-slate block mb-2">
                How we help
              </span>
              <h2 className="font-teko text-4xl text-text-obsidian">
                Relocation made simpler
              </h2>
            </div>

            <div className="grid gap-6">
              {SERVICES.map((service) => (
                <div
                  key={service.title}
                  className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] p-8 shadow-soft"
                >
                  <h3 className="font-teko text-2xl text-text-obsidian mb-3">
                    {service.title}
                  </h3>
                  <p className="font-manrope text-sm text-text-slate leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-[#111] text-white p-10">
              <h3 className="font-teko text-3xl mb-4">WeChat</h3>
              <p className="font-manrope text-sm text-[#ccc] leading-relaxed mb-6">
                Prefer to chat on WeChat? Leave your WeChat ID in the form, or
                email us and we will connect with you directly.
              </p>
              <a
                href={mailtoHref}
                className="inline-block font-teko text-xl uppercase text-brand-yellow border-b border-brand-yellow hover:text-white hover:border-white transition-colors"
              >
                Email us to connect on WeChat
              </a>
            </div>
          </div>

          <div className="bg-bg-white p-10 border border-solid border-[rgba(0,0,0,0.05)] shadow-[0_30px_60px_rgba(0,0,0,0.05)] lg:sticky lg:top-28">
            <h2 className="font-teko text-3xl text-text-obsidian mb-2">
              Start your enquiry
            </h2>
            <p className="font-manrope text-sm text-text-slate mb-8">
              Tell us about your move and we will be in touch. You can also{" "}
              <a
                href={mailtoHref}
                className="text-brand-yellow hover:underline"
              >
                email us directly
              </a>
              .
            </p>

            <form className="grid gap-5" onSubmit={handleSubmit}>
              {success && (
                <p className="font-manrope text-sm text-green-700 bg-green-50 p-4 border border-green-200">
                  Thank you — your message has been sent. We will be in touch
                  shortly.
                </p>
              )}
              {error && (
                <p className="font-manrope text-sm text-red-700 bg-red-50 p-4 border border-red-200">
                  {error}
                </p>
              )}
              <div>
                <label
                  htmlFor="concierge-name"
                  className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-2"
                >
                  Name
                </label>
                <input
                  id="concierge-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting}
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope focus:outline-none focus:border-brand-yellow"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label
                  htmlFor="concierge-email"
                  className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-2"
                >
                  Email
                </label>
                <input
                  id="concierge-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope focus:outline-none focus:border-brand-yellow"
                  placeholder="Email address"
                />
              </div>
              <div>
                <label
                  htmlFor="concierge-wechat"
                  className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-2"
                >
                  WeChat ID (optional)
                </label>
                <input
                  id="concierge-wechat"
                  type="text"
                  value={wechat}
                  onChange={(e) => setWechat(e.target.value)}
                  disabled={submitting}
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope focus:outline-none focus:border-brand-yellow"
                  placeholder="Your WeChat ID"
                />
              </div>
              <div>
                <label
                  htmlFor="concierge-message"
                  className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-2"
                >
                  Message
                </label>
                <textarea
                  id="concierge-message"
                  rows={4}
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={submitting}
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope focus:outline-none focus:border-brand-yellow"
                  placeholder="Tell us about your move, preferred areas, schools, or pets…"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full p-4 bg-text-obsidian text-white border-none font-teko text-xl uppercase cursor-pointer transition-all duration-300 hover:bg-brand-yellow hover:text-black disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send enquiry"}
              </button>
            </form>

            <p className="font-manrope text-xs text-text-slate mt-6">
              Or browse our{" "}
              <Link to="/services/rent/properties" className="text-brand-yellow hover:underline">
                available properties
              </Link>{" "}
              and{" "}
              <Link to="/insights" className="text-brand-yellow hover:underline">
                relocation guides
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
